import { type ReactNode } from "react";
import { HalfMoonIcon, LaptopIcon, SunIcon } from "vega-icons";

import { SectionTitle } from "@vega-core/components/Layout/SectionTitle";
import { useTheme } from "@vega-core/context/ThemeContext/ThemeContext";
import { m } from "@vega-core/paraglide/messages";
import { type ThemesList } from "@vega-core/themeConfig/themesModel";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { SettingsSectionLayout } from "../components/SettingsSectionLayout";

type ThemeButton = {
  id: "auto" | "dark" | "light";
  label: string;
  icon: ReactNode;
  isActive: (theme: ThemesList, useSystemTheme: boolean) => boolean;
  onClick: (
    changeTheme: (theme: ThemesList) => void,
    setUseSystemTheme: (use: boolean) => void,
    theme: ThemesList,
  ) => void;
};

type Props = {
  title?: string;
};

export const Display = ({ title }: Props = {}) => {
  const { changeTheme, setUseSystemTheme, theme, useSystemTheme } = useTheme();

  const themeButtons: ThemeButton[] = [
    {
      id: "light",
      label: "Light",
      icon: <SunIcon className="w-8 h-8" />,
      isActive: () => !useSystemTheme && theme === "light",
      onClick: (changeTheme, setUseSystemTheme) => {
        setUseSystemTheme(false);
        changeTheme("light");
      },
    },
    {
      id: "dark",
      label: "Dark",
      icon: <HalfMoonIcon className="w-8 h-8" />,
      isActive: () => !useSystemTheme && theme === "dark",
      onClick: (changeTheme, setUseSystemTheme) => {
        setUseSystemTheme(false);
        changeTheme("dark");
      },
    },
    {
      id: "auto",
      label: "System",
      icon: <LaptopIcon className="w-8 h-8" />,
      isActive: () => useSystemTheme,
      onClick: (changeTheme, setUseSystemTheme, theme) => {
        setUseSystemTheme(true);
        changeTheme(theme);
      },
    },
  ];

  return (
    <SettingsSectionLayout bordered={!title} title={title}>
      <SectionTitle>{m.settings_colorMode()}</SectionTitle>
      <p className="text-text/primary">{m.settings_personalSettingsTitle()}</p>

      <div className="flex flex-col sm:flex-row gap-4">
        {themeButtons.map((button) => {
          const isActive =
            button.isActive(theme, useSystemTheme) ||
            (theme === "default" && button.id === themeButtons[0].id);

          return (
            <button
              aria-pressed={isActive}
              className={cnTwMerge(
                "grow shrink basis-0 p-6 rounded-lg flex flex-col justify-center items-center gap-4 border",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border/action",
                isActive
                  ? "border-border/action text-text/primary"
                  : "border-border/primary text-text/secondary",
              )}
              data-testid={`theme-button-${button.id}`}
              key={button.id}
              onClick={() =>
                button.onClick(changeTheme, setUseSystemTheme, theme)
              }
              type="button"
            >
              <div className="flex justify-center items-center w-8 h-8">
                {button.icon}
              </div>
              <div className="flex flex-col items-center justify-start h-5">
                <span className="body-base-medium">{button.label}</span>
              </div>
            </button>
          );
        })}
      </div>
    </SettingsSectionLayout>
  );
};
